import React, { useEffect, useState } from 'react';
import { Box, Chip, FormControl, MenuItem, Select, SelectChangeEvent, InputLabel, OutlinedInput, Checkbox, ListItemText, TextField, InputAdornment, Tooltip, IconButton } from '@mui/material';
import { Icon } from '@iconify/react';
import { InfoIcon } from 'lucide-react';
import DataTable, { Column, RowAction, FilterChip } from './DataTable';
import { shouldShowResendEmail } from '../utils/passwordResetHistory';
interface PartnerUser {
  id: number;
  name: string;
  fatherName: string;
  idNumber: string;
  email: string;
  phone: string;
  username: string;
  partner: string;
  status: 'active' | 'inactive';
  userType: 'maker' | 'checker';
  createdAt: string;
}
interface PartnerUsersTableProps {
  users: PartnerUser[];
  onEditUser: (user: PartnerUser) => void;
  searchQuery: string;
  partnerFilter: string | null;
  onStatusChange?: (user: PartnerUser, newStatus: 'active' | 'inactive') => void;
  onResetPassword?: (user: PartnerUser) => void;
  onViewResetHistory?: (user: PartnerUser) => void;
  onResendEmail?: (user: PartnerUser) => void;
}
const PARTNERS = ['GTBank Ghana', 'First Bank Nigeria', 'Flutterwave Nigeria', 'M-Pesa Kenya', 'Orange Money Senegal'];
const PartnerUsersTable: React.FC<PartnerUsersTableProps> = ({
  users,
  onEditUser,
  searchQuery,
  partnerFilter,
  onStatusChange,
  onResetPassword,
  onViewResetHistory,
  onResendEmail
}) => {
  const [filters, setFilters] = useState({
    partners: partnerFilter ? [partnerFilter] : [] as string[],
    status: 'all',
    userType: 'all',
    dateFrom: '',
    dateTo: '',
    search: searchQuery || '',
    name: '',
    fatherName: '',
    idNumber: '',
    email: '',
    phone: '',
    username: ''
  });
  const [loading, setLoading] = useState(false);
  const [columnFilters, setColumnFilters] = useState<{
    [key: string]: string | string[];
  }>({});
  // Handle column filter change
  const handleColumnFilterChange = (columnId: string, value: string | string[]) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnId]: value
    }));
  };
  // Handle text filter change
  const handleTextFilterChange = (field: string, value: string) => {
    setFilters({
      ...filters,
      [field]: value
    });
  };
  // Update search when searchQuery prop changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      search: searchQuery || ''
    }));
  }, [searchQuery]);
  // Apply filters to the data
  const filteredUsers = users.filter(user => {
    // Apply search query
    if (filters.search) {
      const query = filters.search.toLowerCase();
      if (!user.name.toLowerCase().includes(query) && !user.email.toLowerCase().includes(query) && !user.idNumber.toLowerCase().includes(query) && !user.phone.toLowerCase().includes(query) && !user.username.toLowerCase().includes(query) && !user.partner.toLowerCase().includes(query)) {
        return false;
      }
    }
    // Column-specific text filters
    if (filters.name && !user.name.toLowerCase().includes(filters.name.toLowerCase())) {
      return false;
    }
    if (filters.fatherName && !user.fatherName.toLowerCase().includes(filters.fatherName.toLowerCase())) {
      return false;
    }
    if (filters.idNumber && !user.idNumber.toLowerCase().includes(filters.idNumber.toLowerCase())) {
      return false;
    }
    if (filters.email && !user.email.toLowerCase().includes(filters.email.toLowerCase())) {
      return false;
    }
    if (filters.phone && !user.phone.toLowerCase().includes(filters.phone.toLowerCase())) {
      return false;
    }
    if (filters.username && !user.username.toLowerCase().includes(filters.username.toLowerCase())) {
      return false;
    }
    // Partner filter
    if (filters.partners.length > 0 && !filters.partners.includes(user.partner)) {
      return false;
    }
    // Status filter
    if (filters.status !== 'all' && user.status !== filters.status) {
      return false;
    }
    // User type filter from main filters
    if (filters.userType !== 'all' && user.userType !== filters.userType) {
      return false;
    }
    // User type filter from column filter
    if (columnFilters.userType && columnFilters.userType !== 'all' && user.userType !== columnFilters.userType) {
      return false;
    }
    // Status filter from column filter
    if (columnFilters.status && columnFilters.status !== 'all' && user.status !== columnFilters.status) {
      return false;
    }
    // Partner filter from column filter
    if (columnFilters.partner && columnFilters.partner !== 'all' && user.partner !== columnFilters.partner) {
      return false;
    }
    // Date filters
    if (filters.dateFrom) {
      const userDate = new Date(user.createdAt);
      const fromDate = new Date(filters.dateFrom);
      if (userDate < fromDate) {
        return false;
      }
    }
    if (filters.dateTo) {
      const userDate = new Date(user.createdAt);
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (userDate > toDate) {
        return false;
      }
    }
    return true;
  });
  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      partners: [],
      status: 'all',
      userType: 'all',
      dateFrom: '',
      dateTo: '',
      search: '',
      name: '',
      fatherName: '',
      idNumber: '',
      email: '',
      phone: '',
      username: ''
    });
    setColumnFilters({});
  };
  // Handle search change
  const handleSearchChange = (value: string) => {
    setFilters({
      ...filters,
      search: value
    });
  };
  // Define filter chips
  const filterChips: FilterChip[] = [...(filters.name ? [{
    id: 'name',
    label: 'Name',
    value: filters.name,
    onDelete: () => handleTextFilterChange('name', '')
  }] : []), ...(filters.fatherName ? [{
    id: 'fatherName',
    label: 'Department',
    value: filters.fatherName,
    onDelete: () => handleTextFilterChange('fatherName', '')
  }] : []), ...(filters.idNumber ? [{
    id: 'idNumber',
    label: 'Passport/ID',
    value: filters.idNumber,
    onDelete: () => handleTextFilterChange('idNumber', '')
  }] : []), ...(filters.email ? [{
    id: 'email',
    label: 'Email',
    value: filters.email,
    onDelete: () => handleTextFilterChange('email', '')
  }] : []), ...(filters.phone ? [{
    id: 'phone',
    label: 'Phone',
    value: filters.phone,
    onDelete: () => handleTextFilterChange('phone', '')
  }] : []), ...(filters.username ? [{
    id: 'username',
    label: 'Username',
    value: filters.username,
    onDelete: () => handleTextFilterChange('username', '')
  }] : []), ...filters.partners.map(partner => ({
    id: `partner-${partner}`,
    label: 'Partner',
    value: partner,
    onDelete: () => setFilters({
      ...filters,
      partners: filters.partners.filter(p => p !== partner)
    })
  })), ...(filters.status !== 'all' ? [{
    id: 'status',
    label: 'Status',
    value: filters.status,
    onDelete: () => setFilters({
      ...filters,
      status: 'all'
    })
  }] : []), ...(filters.userType !== 'all' ? [{
    id: 'userType',
    label: 'User Type',
    value: filters.userType === 'maker' ? 'Maker' : 'Checker',
    onDelete: () => setFilters({
      ...filters,
      userType: 'all'
    })
  }] : []), ...(filters.dateFrom ? [{
    id: 'dateFrom',
    label: 'From',
    value: new Date(filters.dateFrom).toLocaleDateString(),
    onDelete: () => setFilters({
      ...filters,
      dateFrom: ''
    })
  }] : []), ...(filters.dateTo ? [{
    id: 'dateTo',
    label: 'To',
    value: new Date(filters.dateTo).toLocaleDateString(),
    onDelete: () => setFilters({
      ...filters,
      dateTo: ''
    })
  }] : []), ...Object.entries(columnFilters).filter(([key, value]) => value !== 'all' && value !== '').map(([key, value]) => ({
    id: `column-${key}-${value}`,
    label: columns.find(col => col.id === key)?.label || key,
    value: typeof value === 'string' ? value : value.join(', '),
    onDelete: () => handleColumnFilterChange(key, key === 'status' || key === 'userType' || key === 'partner' ? 'all' : '')
  }))];
  // Define columns
  const columns: Column<PartnerUser>[] = [{
    id: 'name',
    label: 'Name',
    renderFilter: () => <TextField size="small" placeholder="Search name" value={filters.name} onChange={e => handleTextFilterChange('name', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1342">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1343" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1341" />
  }, {
    id: 'fatherName',
    label: 'Department',
    renderFilter: () => <TextField size="small" placeholder="Search department" value={filters.fatherName} onChange={e => handleTextFilterChange('fatherName', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1345">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1346" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1344" />
  }, {
    id: 'idNumber',
    label: 'Passport/ID',
    renderFilter: () => <TextField size="small" placeholder="Search Passport/ID" value={filters.idNumber} onChange={e => handleTextFilterChange('idNumber', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1348">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1349" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1347" />
  }, {
    id: 'email',
    label: 'Email',
    renderFilter: () => <TextField size="small" placeholder="Search email" value={filters.email} onChange={e => handleTextFilterChange('email', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1351">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1352" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1350" />
  }, {
    id: 'phone',
    label: 'Phone',
    renderFilter: () => <TextField size="small" placeholder="Search phone" value={filters.phone} onChange={e => handleTextFilterChange('phone', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1354">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1355" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1353" />
  }, {
    id: 'username',
    label: 'Username',
    renderFilter: () => <TextField size="small" placeholder="Search username" value={filters.username} onChange={e => handleTextFilterChange('username', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1357">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1358" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1356" />
  }, {
    id: 'partner',
    label: 'Partner',
    renderCell: row => <Chip label={row.partner} size="small" sx={{
      backgroundColor: '#E0F7FF',
      color: '#0EA5E9',
      height: 24
    }} data-id="element-1359" />,
    renderFilter: () => <FormControl variant="outlined" size="small" fullWidth data-id="element-1360">
          <Select value={columnFilters.partner || 'all'} onChange={e => handleColumnFilterChange('partner', e.target.value)} displayEmpty sx={{
        borderRadius: 1,
        fontSize: '0.8rem',
        height: 36
      }} data-id="element-1361">
            <MenuItem value="all" data-id="element-1362">All Partners</MenuItem>
            {PARTNERS.map(partner => <MenuItem key={partner} value={partner} data-id="element-1363">
                {partner}
              </MenuItem>)}
          </Select>
        </FormControl>
  }, {
    id: 'userType',
    label: 'User Type',
    renderCell: row => <Chip label={row.userType === 'maker' ? 'Maker' : 'Checker'} size="small" sx={{
      backgroundColor: row.userType === 'maker' ? 'rgba(6, 182, 212, 0.1)' : 'rgba(139, 92, 246, 0.1)',
      color: row.userType === 'maker' ? '#06B6D4' : '#8B5CF6',
      height: 24,
      fontWeight: 600
    }} data-id="element-1364" />,
    renderFilter: () => <FormControl variant="outlined" size="small" fullWidth data-id="element-1365">
          <Select value={columnFilters.userType || 'all'} onChange={e => handleColumnFilterChange('userType', e.target.value)} displayEmpty sx={{
        borderRadius: 1,
        fontSize: '0.8rem',
        height: 36
      }} data-id="element-1366">
            <MenuItem value="all" data-id="element-1367">All Types</MenuItem>
            <MenuItem value="maker" data-id="element-1368">Maker</MenuItem>
            <MenuItem value="checker" data-id="element-1369">Checker</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'status',
    label: 'Status',
    renderCell: row => <Chip label={row.status === 'active' ? 'Active' : 'Inactive'} size="small" sx={{
      backgroundColor: row.status === 'active' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
      color: row.status === 'active' ? '#10B981' : '#EF4444',
      height: 24
    }} data-id="element-1370" />,
    renderFilter: () => <FormControl variant="outlined" size="small" fullWidth data-id="element-1371">
          <Select value={columnFilters.status || 'all'} onChange={e => handleColumnFilterChange('status', e.target.value)} displayEmpty sx={{
        borderRadius: 1,
        fontSize: '0.8rem',
        height: 36
      }} data-id="element-1372">
            <MenuItem value="all" data-id="element-1373">All Status</MenuItem>
            <MenuItem value="active" data-id="element-1374">Active</MenuItem>
            <MenuItem value="inactive" data-id="element-1375">Inactive</MenuItem>
          </Select>
        </FormControl>
  }];
  // Define row actions
  const rowActions: RowAction<PartnerUser>[] = [{
    icon: 'lucide:eye',
    label: 'View',
    onClick: user => console.log('View user', user)
  }, {
    icon: 'lucide:edit',
    label: 'Edit',
    onClick: onEditUser
  }, {
    icon: 'lucide:mail',
    label: 'Resend Email',
    onClick: user => onResendEmail?.(user),
    condition: user => shouldShowResendEmail(user)
  }, {
    icon: 'lucide:x-circle',
    label: 'Deactivate',
    onClick: user => onStatusChange?.(user, 'inactive'),
    condition: user => user.status === 'active'
  }, {
    icon: 'lucide:check-circle',
    label: 'Activate',
    onClick: user => onStatusChange?.(user, 'active'),
    condition: user => user.status === 'inactive'
  }, {
    icon: 'lucide:key',
    label: 'Reset Password',
    onClick: user => onResetPassword?.(user)
  }, {
    icon: 'lucide:clock',
    label: 'Password Reset History',
    onClick: user => onViewResetHistory?.(user)
  }, {
    icon: 'lucide:history',
    label: 'Audit Log',
    onClick: user => console.log('View audit trail for user', user)
  }];
  // Handle export
  const handleExport = () => {
    console.log('Export data');
  };
  return <DataTable title="Partner Users" data={filteredUsers} columns={columns} rowActions={rowActions} keyField="id" filterChips={filterChips} onClearAllFilters={handleClearFilters} loading={loading} initialVisibleColumns={['name', 'email', 'phone', 'username', 'partner', 'userType', 'status']} onSearchChange={handleSearchChange} searchPlaceholder="Search users by name, email, phone..." searchValue={filters.search} onExport={handleExport} emptyStateMessage="No users found" data-id="element-1376" />;
};
export default PartnerUsersTable;